import { createApi } from "@reduxjs/toolkit/query/react"

import { ApiTypes, UITypes } from "types"

import { baseQueryWithAuth } from "utils/api"
import { prepareQueryParams } from "utils/prepareQueryParams"

export const referralsApi = createApi({
  reducerPath: "referrals",
  tagTypes: ["referrals"],
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    getReferralAgentTrends: builder.query<ApiTypes.Res.ReferralAgentDashboardTrends, void>({
      query: () => ({ url: `/referral-agent/dashboard/widgets/trends` }),
    }),
    getReferralAgentLinkQrFunnel: builder.query<ApiTypes.Res.ReferralAgentDashboardLinkQrFunnel, void>({
      query: () => ({ url: `/referral-agent/dashboard/widgets/link-qr-funnel` }),
    }),
    getReferralAgentPlacementFunnel: builder.query<ApiTypes.Res.ReferralAgentPlacementFunnel, void>({
      query: () => ({ url: `/referral-agent/dashboard/widgets/placement-funnel` }),
    }),
    getReferralAgentReferralStats: builder.query<ApiTypes.Res.ReferralAgentReferralStats, void>({
      query: () => ({ url: `/referral-agent/dashboard/widgets/referral-stats` }),
      // shares the referrals tag so the counts refresh after a referral is added, edited, confirmed or denied
      providesTags: ["referrals"],
    }),
    getReferralAgentMonthlyPlacements: builder.query<ApiTypes.Res.ReferralAgentDashboardMonthlyPlacements, number>({
      query: period => ({ url: `/referral-agent/dashboard/widgets/monthly-placements`, params: { period } }),
    }),
    getVendorReferralTrends: builder.query<ApiTypes.Res.VendorReferralTrends, number>({
      query: period => ({ url: `/admin/referrals/widgets/trends`, params: { period } }),
    }),
    getReferrals: builder.query<ApiTypes.Res.Referrals, UITypes.Params>({
      query: arg => {
        return {
          url: `/referrals`,
          method: "GET",
          params: prepareQueryParams(arg),
        }
      },
      providesTags: ["referrals"],
    }),
    getArchivedReferrals: builder.query<ApiTypes.Res.Referrals, UITypes.Params>({
      query: arg => {
        return {
          url: `/referrals/archived`,
          method: "GET",
          params: prepareQueryParams(arg),
        }
      },
      providesTags: ["referrals"],
    }),
    getReferral: builder.query<ApiTypes.Res.Referral, number>({
      query: id => {
        return {
          url: `/referrals/${id}`,
          method: "GET",
        }
      },
    }),
    createReferral: builder.mutation<ApiTypes.Res.Referral, ApiTypes.Req.CreateReferral>({
      query: body => {
        return {
          url: `/referrals`,
          method: "POST",
          body,
        }
      },
      invalidatesTags: ["referrals"],
    }),
    updateReferral: builder.mutation<ApiTypes.Res.Referral, { id: number; body: ApiTypes.Req.CreateReferral }>({
      query: data => {
        return {
          url: `/referrals/${data.id}`,
          method: "PUT",
          body: data.body,
        }
      },
      invalidatesTags: ["referrals"],
    }),
    confirmReferral: builder.mutation<void, number>({
      query: id => {
        return {
          url: `/referrals/${id}/confirm`,
          method: "POST",
        }
      },
      invalidatesTags: ["referrals"],
    }),
    denyReferral: builder.mutation<void, number>({
      query: id => {
        return {
          url: `/referrals/${id}/deny`,
          method: "POST",
        }
      },
      invalidatesTags: ["referrals"],
    }),
    getAdminReferrals: builder.query<ApiTypes.Res.VendorReferralAgents, UITypes.Params>({
      query: arg => {
        return {
          url: `/admin/referrals`,
          method: "GET",
          params: prepareQueryParams(arg),
        }
      },
      providesTags: ["referrals"],
    }),
    getAdminReferral: builder.query<ApiTypes.Res.Referral, number>({
      query: id => {
        return {
          url: `/admin/referrals/${id}`,
          method: "GET",
        }
      },
      providesTags: ["referrals"],
    }),
    assignReferral: builder.mutation<
      ApiTypes.Res.ReferralAssignment,
      { id: number; body: ApiTypes.Req.AssignReferral }
    >({
      query: data => {
        return {
          url: `/admin/referrals/${data.id}/assign`,
          method: "PUT",
          body: data.body,
        }
      },
      invalidatesTags: ["referrals"],
    }),
    updateReferralClientDetails: builder.mutation<
      ApiTypes.Res.Referral,
      { id: number; body: ApiTypes.Req.UpdateReferralClientDetails }
    >({
      query: data => {
        return {
          url: `/admin/referrals/${data.id}/client-details`,
          method: "PUT",
          body: data.body,
        }
      },
      invalidatesTags: ["referrals"],
    }),
  }),
})

export const {
  useLazyGetReferralsQuery,
  useLazyGetArchivedReferralsQuery,
  useLazyGetReferralQuery,
  useCreateReferralMutation,
  useUpdateReferralMutation,
  useConfirmReferralMutation,
  useDenyReferralMutation,
  useLazyGetAdminReferralsQuery,
  useLazyGetAdminReferralQuery,
  useGetAdminReferralQuery,
  useAssignReferralMutation,
  useUpdateReferralClientDetailsMutation,
  useGetReferralAgentLinkQrFunnelQuery,
  useGetReferralAgentMonthlyPlacementsQuery,
  useGetReferralAgentPlacementFunnelQuery,
  useGetReferralAgentReferralStatsQuery,
  useGetReferralAgentTrendsQuery,
  useGetVendorReferralTrendsQuery,
} = referralsApi
